import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { AlertButton } from "@obg/mat.ui.core";
import {
	AbstractTranslatableAlertService,
	AUTH_TRANSACTION_CODES,
	AuthTransactionAction,
	AuthTransactionSelector,
	CoreState,
	LoginMethod,
	SessionSelector,
	VerificationStatus,
	VerificationStatusRequest,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { TelemetryService } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { combineLatest, merge, Observable, Subscription, timer } from "rxjs";
import { filter, take, tap } from "rxjs/operators";

import {
	CommonAuthTelemetryEventName,
	CommonAuthTelemetryStartType,
	CommonAuthTelemetryVerificationStatus,
} from "../../../sdk";
import { LoginUiFlowService } from "../../login-ui/login-ui-flow.service";
import { SgaLoginUiFlowService } from "../sga-login-flow.service";
import { SgaLoginError } from "../sga-login.model";

const SGA_QUICK_ALERT_ERROR_ALERT_ID = "SGA_QUICK_ALERT_ERROR_ALERT_ID";

@Component({
	selector: "obg-m-quick-sga-login-container",
	templateUrl: "./quick-sga-login.container.html",
	styleUrls: ["./quick-sga-login.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class QuickSgaLoginContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-quick-sga-login") hostCssClass = true;

	@Input() set translationPrefix(value: string | undefined) {
		if (value) {
			this.notificationAlertTitle = `${value}.notification.title`;
		}
		this._translationPrefix = value;
	}
	get translationPrefix(): string | undefined {
		return this._translationPrefix;
	}

	@Output() busy = new EventEmitter<boolean>();
	@Output() loginSubmit = new EventEmitter();

	polling = false;
	hideCancelButton = true;
	errorMessage = "";
	customerCanLogInAt: string | undefined;
	showError = false;
	primaryButtonTranslationKey = "login.bank-id.start";

	private openBankIdApp$: Observable<void> | undefined;
	private notificationAlertTitle: string | undefined;
	private _translationPrefix: string | undefined;
	private isVerificationAborted = false;
	private closeButton: AlertButton = {
		text: "general.close",
		handler: () => this.hidePolling(),
	};
	private isStatusPending = false;
	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreState>,
		private authTransactionAction: AuthTransactionAction,
		private changeDetector: ChangeDetectorRef,
		private authTransactionSelector: AuthTransactionSelector,
		private alertService: AbstractTranslatableAlertService,
		private sessionSelector: SessionSelector,
		private sgaLoginUiFlowService: SgaLoginUiFlowService,
		private telemetryService: TelemetryService,
		private loginUiFlowService: LoginUiFlowService,
		private customerSupportService: CustomerSupportService
	) {}

	ngOnInit() {
		const personalNumberErrors$ = this.sgaLoginUiFlowService.personalNumberErrors$(true).pipe(
			tap((error) => {
				this.showErrorAlert({
					message: `${this.translationPrefix}.${error}`.toLowerCase(),
				});
			})
		);

		const authTransactionSelectorBusy$ = this.store.pipe(
			select(this.authTransactionSelector.isBusy()),
			tap((status) => (status ? this.showPolling() : undefined))
		);

		const hideCancelButton$ = this.sgaLoginUiFlowService
			.initializeAuthTransactionSuccess$()
			.pipe(tap(() => (this.hideCancelButton = false)));

		const loginTransactionsVerificationStatus$ = this.sgaLoginUiFlowService
			.loginTransactionsVerificationStatus$()
			.pipe(
				filter((status) => status !== VerificationStatus.Failed && !this.isVerificationAborted),
				tap((status) => {
					if (status === VerificationStatus.Pending) {
						this.isStatusPending = true;
						this.showPolling();
						return;
					}
					if (status === VerificationStatus.Completed) {
						this.hidePolling();

						this.telemetryService.track({
							event: CommonAuthTelemetryEventName.BankIDVerification,
							LoginMethod: LoginMethod.BankId,
							Status: CommonAuthTelemetryVerificationStatus.Success,
						});
					}

					this.isStatusPending = false;
				})
			);

		const loginTransactionsVerificationFailed$ = combineLatest([
			timer(5000),
			this.sgaLoginUiFlowService
				.loginTransactionsVerificationStatus$()
				.pipe(filter((status) => status === VerificationStatus.Failed)),
		]).pipe(
			filter(() => !this.isVerificationAborted),
			tap(() => {
				this.showVerificationCancelled();
				this.hidePolling();
				this.isStatusPending = false;
			})
		);

		const customerVerificationFail$ = this.sgaLoginUiFlowService.customerVerificationFail$().pipe(
			tap(() => {
				this.showErrorAlert({
					message: `${this.translationPrefix}.error.text`,
				});

				this.isVerificationAborted = true;
				this.hidePolling();
			})
		);

		const cancelLastVerification$ = this.sgaLoginUiFlowService.cancelLastVerification$().pipe(
			filter(() => this.polling && !this.isVerificationAborted),
			tap(() => {
				this.showVerificationCancelled();
				this.hidePolling();
				this.isVerificationAborted = true;
			})
		);

		const initializeVerificationFail$ = this.sgaLoginUiFlowService.initializeVerificationFail$().pipe(
			tap(() => {
				this.showErrorAlert({
					message: "sga-login-transaction.initialize.fail",
					buttons: [
						{
							text: "general.ok",
						},
					],
				});

				this.isVerificationAborted = true;
				this.hidePolling();
			})
		);

		const invalidCredentialsError$ = this.sgaLoginUiFlowService.invalidCredentialsError$().pipe(
			tap(() => {
				this.errorMessage = "sga-login-transaction.error.personal-number-not-found";
				this.showError = true;
				this.hidePolling();
			})
		);

		const genericLoginError$ = this.sgaLoginUiFlowService.genericLoginError$().pipe(
			tap((errors) => {
				this.errorMessage = `${this.translationPrefix}.${errors || "error.text"}`.toLowerCase();
				this.showError = true;
				this.hidePolling();
			})
		);

		const sessionLimitExpired$ = this.loginUiFlowService.sessionLimitExpired$().pipe(
			tap((error) => {
				this.customerCanLogInAt = error.customerCanLogInAt;
				this.errorMessage = `${this.translationPrefix}.${error?.code || "error.text"}`.toLowerCase();
			})
		);

		const getVerificationStatus$ = this.store
			.select<VerificationStatusRequest | undefined>(RouteSelector.getQueryParams)
			.pipe(
				filter((params) => !!(params?.transactionId && !this.polling)),
				take(1),
				tap(({ transactionId }: VerificationStatusRequest) =>
					this.store.dispatch(this.authTransactionAction.getVerificationStatus({ transactionId }))
				)
			);
		this.openBankIdApp$ = this.sgaLoginUiFlowService.openBankIdApp$(
			this.polling,
			this.isVerificationAborted,
			this.translationPrefix
		);

		const authBusy$ = this.store.pipe(
			select(this.sessionSelector.isBusy()),
			tap((isBusy) => this.busy.emit(isBusy))
		);

		this.data$$ = merge(
			this.openBankIdApp$,
			authTransactionSelectorBusy$,
			loginTransactionsVerificationStatus$,
			loginTransactionsVerificationFailed$,
			personalNumberErrors$,
			customerVerificationFail$,
			cancelLastVerification$,
			initializeVerificationFail$,
			invalidCredentialsError$,
			getVerificationStatus$,
			genericLoginError$,
			sessionLimitExpired$,
			hideCancelButton$,
			authBusy$
		).subscribe(() => this.changeDetector.markForCheck());

		this.telemetryService.track({
			event: CommonAuthTelemetryEventName.BankIDFormOpen,
			LoginMethod: LoginMethod.BankId,
		});
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		if (this.isStatusPending) {
			this.store.dispatch(this.authTransactionAction.cancelLastVerification());
		}
	}

	submitCancel() {
		this.store.dispatch(this.authTransactionAction.cancelLastVerification());
	}

	login() {
		// eslint-disable-next-line rxjs/no-ignored-subscription
		this.openBankIdApp$?.subscribe();
		this.loginSubmit.emit();

		this.telemetryService.track({
			event: CommonAuthTelemetryEventName.BankIDStart,
			LoginMethod: LoginMethod.BankId,
			StartType: CommonAuthTelemetryStartType.SameDevice,
		});
	}

	showPolling() {
		this.errorMessage = "";
		this.customerCanLogInAt = "";
		this.showError = false;
		this.polling = true;
		this.hideCancelButton = true;
		this.isVerificationAborted = false;
		this.primaryButtonTranslationKey = `${this.translationPrefix}.initializing`;
	}

	hidePolling() {
		this.polling = false;
		this.primaryButtonTranslationKey = "login.bank-id.start";
		this.hideCancelButton = true;
		this.changeDetector.markForCheck();
	}

	private showVerificationCancelled() {
		this.isVerificationAborted = true;
		this.showErrorAlert({
			buttons: [this.closeButton],
			message: `${this.translationPrefix}.${AUTH_TRANSACTION_CODES.cancelLastVerification.success}`.toLowerCase(),
		});
	}

	private showErrorAlert(error: SgaLoginError) {
		this.alertService.open(
			{
				id: SGA_QUICK_ALERT_ERROR_ALERT_ID,
				message: error.message,
				title: error.title ? error.title : this.notificationAlertTitle,
				buttons: error.buttons
					? error.buttons
					: [
							this.closeButton,
							{
								text: "general.contact",
								handler: () => this.customerSupportService.openSupportPageInNewTab(),
							},
						],
			},
			[TranslationGroup.Common]
		);
	}
}
